import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { deleteDataAPI, getDataAPI, postDataAPI, putDataAPI } from '../../Helpers/FetchAPI/index.js';
import useUser from '../../Hooks/useUser.js';
import { logger } from '../../utils/logger.js';

const initState = {
  title: '',
  description: '',
  requiredScore: 0,
  type: 'weekly_tools', // 'weekly_tools' or 'assessments'
  relatedWorkbooks: [], // Array of workbook ObjectIds
  items: [], // Array of ToolsItem objects
  status: 'draft' // 'draft' or 'publish'
};

// Initial state for a tool item
const initItemState = {
  type: 'content', // 'radio', 'input', 'textarea', 'checkbox', 'rating', 'content'
  title: '',
  options: [],
  priority: 1,
  points: 1,
  subtype: '' // For grouping items (e.g., 'A', 'B', etc.)
};

export const AdminToolsContext = createContext({
  initState,
  initItemState,
  toolsData: [],
  setToolsData: () => {},
  isLoading: false,
  error: null,
  tool: initState,
  setTool: () => {},
  handleAddTool: () => {},
  handleUpdateTool: () => {},
  handleDeleteTool: () => {},
  handleAddToolItem: () => {},
  handleUpdateToolItem: () => {},
  handleDeleteToolItem: () => {},
  handleReorderToolItems: () => {},
  refreshTools: () => {}
});

const AdminToolsContextProvider = ({ children }) => {
  const { currentUser } = useUser();
  const [toolsData, setToolsData] = useState([]);
  const [tool, setTool] = useState(initState);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const getTools = useCallback(async () => {
    await getDataAPI('/api/tools')
      .then((res) => {
        // Handle different response structures
        const tools = res.data.data ;
        setToolsData(Array.isArray(tools) ? tools : []);
        setIsLoading(false);
      })
      .catch((err) => {
        logger.error('Error getting tools', err);
        setError(err);
        setIsLoading(false);
      });
  }, []);

  // Expose refresh function for manual refetching
  const refreshTools = useCallback(() => {
    setIsLoading(true);
    getTools();
  }, [getTools]);

  useEffect(() => {
    let isActive = true;

    if (isActive) {
      if (currentUser) {
        setIsLoading(true);
        getTools();
      }
    }

    return () => {
      isActive = false;
    };
  }, [currentUser, getTools]);

  const handleAddTool = useCallback(
    async (callback) => {
      await postDataAPI('/api/tools', {
        ...tool,
        userId: currentUser._id
      }).then((res) => {
        const newTool =  res.data.data;

        setToolsData((prev) => [...prev, newTool]);
        setTool(initState);
        if (callback) {
          callback(newTool);
        }
      });
    },
    [tool, currentUser]
  );

  const handleUpdateTool = useCallback(
    async (callback) => {
      await putDataAPI(`/api/tools/${tool._id}`, tool).then((res) => {
        const { success, data: updatedTool } = res.data;

        if (success) {
          setToolsData((prev) => {
            const updatedTools = prev.map((item) => {
              if (item._id === tool._id) {
                return updatedTool || { ...item, ...tool };
              }

              return item;
            });

            return updatedTools;
          });

          // Update the current tool state with the backend response
          setTool(updatedTool || tool);

          if (callback) {
            callback();
          }
        }
      });
    },
    [tool]
  );

  const handleDeleteTool = useCallback(async (id) => {
    await deleteDataAPI(`/api/tools/${id}`).then((res) => {
      const { success } = res.data;

      if (success) {
        setToolsData((prev) => [...prev.filter((item) => item._id !== id)]);
      }
    });
  }, []);

  const handleAddToolItem = useCallback(
    async (toolId, item, callback) => {
      await postDataAPI(`/api/tools/${toolId}/items`, item)
        .then((res) => {

          const updatedTool = res.data.tool || res.data.data;
          const success = res.data.success;

          if (success && updatedTool) {
            // Update the specific tool in the toolsData array
            setToolsData((prev) =>
              prev.map((t) => (t._id === toolId ? updatedTool : t))
            );
            setTool(updatedTool);
            if (callback) {
              callback(updatedTool);
            }
          } else {
            // Fallback: refresh all tools
            getTools();
            if (callback) {
              callback(null, new Error("No updated tool in response"));
            }
          }
        })
        .catch((err) => {
          logger.error('Error adding tool item', err);
          if (callback) callback(null, err);
        });
    },
    [getTools]
  );

  const handleUpdateToolItem = useCallback(
    async (toolId, itemId, item, callback) => {

      await putDataAPI(`/api/tools/${toolId}/items/${itemId}`, item)
        .then((res) => {
          const success = res.data.success;
          const updatedItem = res.data.data;

          if (success && updatedItem) {
            // Update the item within the tool's items array
            setToolsData((prev) =>
              prev.map((t) => {
                if (t._id === toolId) {
                  return {
                    ...t,
                    items: t.items.map((i) => (i._id === itemId ? updatedItem : i))
                  };
                }
                return t;
              })
            );

            setTool((prev) => {
              if (prev && prev._id === toolId) {
                return {
                  ...prev,
                  items: prev.items.map((i) => (i._id === itemId ? updatedItem : i))
                };
              }
              return prev;
            });

            if (callback) {
              callback(null); // Success, no error
            }
          } else {
            if (callback) callback(new Error("No updated item in response"));
          }
        })
        .catch((err) => {
          logger.error('Error updating tool item', err);
          if (callback) callback(err);
        });
    },
    []
  );

  const handleDeleteToolItem = useCallback(
    async (toolId, itemId, callback) => {
      await deleteDataAPI(`/api/tools/${toolId}/items/${itemId}`)
        .then((res) => {
          const { success, tool: updatedTool } = res.data;
          if (success) {
            if (updatedTool) {
              setToolsData((prev) =>
                prev.map((t) => (t._id === toolId ? updatedTool : t))
              );
              setTool(updatedTool);
            }
            if (callback) {
              callback(updatedTool);
            }
          }
        })
        .catch((err) => {
          logger.error('Error deleting tool item', err);
          if (callback) callback(null, err);
        });
    },
    []
  );

  const handleReorderToolItems = useCallback(
    async (toolId, items, callback) => {
      await putDataAPI(`/api/tools/${toolId}/items/reorder`, { items })
        .then((res) => {
          const { success, tool: updatedTool } = res.data;
          if (success) {
            if (updatedTool) {
              setToolsData((prev) =>
                prev.map((t) => (t._id === toolId ? updatedTool : t))
              );
              setTool(updatedTool);
            }
            if (callback) {
              callback(updatedTool);
            }
          }
        })
        .catch((err) => {
          logger.error('Error reordering tool items', err);
          if (callback) callback(null, err);
        });
    },
    []
  );

  const value = useMemo(() => {
    return {
      initState,
      initItemState,
      toolsData,
      setToolsData,
      isLoading,
      error,
      tool,
      setTool,
      handleAddTool,
      handleUpdateTool,
      handleDeleteTool,
      handleAddToolItem,
      handleUpdateToolItem,
      handleDeleteToolItem,
      handleReorderToolItems,
      refreshTools
    };
  }, [
    tool,
    toolsData,
    error,
    handleAddTool,
    handleUpdateTool,
    handleDeleteTool,
    handleAddToolItem,
    handleUpdateToolItem,
    handleDeleteToolItem,
    handleReorderToolItems,
    refreshTools,
    isLoading
  ]);

  return <AdminToolsContext.Provider value={value}>{children}</AdminToolsContext.Provider>;
};

export default AdminToolsContextProvider;

